/**
 * Unified Authentication Service
 * Integrates with Convex creatorAuth for secure authentication
 * Supports both WebAuthn (passkey) and password-based authentication
 */

import {
  credentialToAuthenticationJSON,
  credentialToRegistrationJSON,
  base64urlToArrayBuffer,
  arrayBufferToBase64url,
} from "@/lib/webauthn";
import type { AuthLevel, SessionData } from "@/types";

// Keys from environment (embedded at build time)
const MASTER_KEY = (import.meta.env as any)['VITE_MASTER_KEY'] || "";
const CREATOR_KEY = (import.meta.env as any)['VITE_CREATOR_KEY'] || "";
const KERNEL_BASE = (import.meta.env as any)['VITE_KERNEL_URL'] || "http://127.0.0.1:3340";

const SESSION_KEY = "binetic_auth_session";
const SESSION_EXPIRY_HOURS = 4;

// Helper function
async function readJsonOrText(r: Response): Promise<any> {
  const text = await r.text();
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, error: text };
  }
}

interface ConvexAuthConfig {
  convexUrl: string;
}

interface ConvexCredential {
  _id: string;
  credentialId: string;
  authorityLevel: string;
  authenticatorType: string;
  transports?: string[];
  status: string;
  createdAt: number;
}

interface ConvexSession {
  sessionId: string;
  authorityLevel: string;
  absoluteAuthority: boolean;
  expiresAt: number;
}

class ConvexAuthClient {
  private url: string;

  constructor(url: string = "http://127.0.0.1:3210") {
    this.url = url.replace(/\/$/, "");
  }

  /**
   * Call Convex query via HTTP
   */
  async query<T>(functionPath: string, args: Record<string, unknown> = {}): Promise<T> {
    const urlWithProtocol = this.url.includes('http') ? this.url : `http://${this.url}`;
    const response = await fetch(`${urlWithProtocol}/api/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        path: functionPath,
        args,
        format: "json",
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Convex query failed (${response.status}): ${text}`);
    }

    const data = await response.json();

    if (data.status === "error") {
      throw new Error(data.errorMessage || "Query failed");
    }

    return data.value as T;
  }

  /**
   * Call Convex mutation via HTTP
   */
  async mutation<T>(functionPath: string, args: Record<string, unknown> = {}): Promise<T> {
    const urlWithProtocol = this.url.includes('http') ? this.url : `http://${this.url}`;
    const response = await fetch(`${urlWithProtocol}/api/mutation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        path: functionPath,
        args,
        format: "json",
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Convex mutation failed (${response.status}): ${text}`);
    }

    const data = await response.json();

    if (data.status === "error") {
      throw new Error(data.errorMessage || "Mutation failed");
    }

    return data.value as T;
  }

  /**
   * Check if creator credential is required/available
   */
  async hasCreatorCredential(): Promise<{ hasCreator: boolean }> {
    return this.query("creatorAuth:hasCreatorCredential", {});
  }

  /**
   * Validate a session with Convex
   */
  async validateSession(sessionId: string): Promise<{ valid: boolean; authorityLevel?: string; absoluteAuthority?: boolean }> {
    return this.query("creatorAuth:validateSession", { sessionId });
  }

  /**
   * Create an authenticated session via Convex
   */
  async createConvexSession(credentialId: string, challenge: string, absoluteAuthority: boolean): Promise<ConvexSession> {
    const result = await this.mutation<{
      success: boolean;
      sessionId?: string;
      expiresAt?: number;
      authorityLevel?: string;
      error?: string;
    }>("creatorAuth:createSession", {
      credentialId,
      challenge,
      absoluteAuthority,
    });

    if (!result.success || !result.sessionId) {
      throw new Error(result.error || "Failed to create session");
    }

    return {
      sessionId: result.sessionId!,
      authorityLevel: result.authorityLevel!,
      absoluteAuthority: absoluteAuthority,
      expiresAt: result.expiresAt || Date.now() + 60 * 60 * 1000,
    };
  }

  /**
   * Register password key as credential in Convex
   */
  async registerPasswordKey(password: string): Promise<void> {
    // Determine authority level based on password
    const authLevel = this.verifyPassword(password);
    if (authLevel === "none") {
      throw new Error("Invalid key - neither Master nor Creator key");
    }

    // This would typically be stored in Convex, but for password-only auth
    // we'll handle it differently in practice. For now, mark as verified.
    console.log(`Password verified as ${authLevel} level`);
  }

  /**
   * Verify password
   */
  verifyPassword(pwd: string): AuthLevel {
    if (pwd === CREATOR_KEY && CREATOR_KEY) return "creator";
    if (pwd === MASTER_KEY && MASTER_KEY) return "master";
    return "none";
  }

  /**
   * Update credential counter after use
   */
  async updateCredentialCounter(credentialId: string, newCounter: number): Promise<void> {
    await this.mutation("creatorAuth:updateCounter", {
      credentialId,
      newCounter,
    });
  }

  /**
   * End a session
   */
  async endSession(sessionId: string): Promise<void> {
    await this.mutation("creatorAuth:endSession", { sessionId });
  }

  /**
   * Test connection to Convex
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.hasCreatorCredential();
      return true;
    } catch {
      return false;
    }
  }
}

class AuthService {
  private session: SessionData | null = null;
  private listeners: Set<() => void> = new Set();
  private convexClient: ConvexAuthClient | null = null;
  private useConvexAuth: boolean = false;

  constructor() {
    this.loadSession();
    this.sessionCheckInterval();
  }

  /**
   * Set Convex URL for auth
   */
  setConvexUrl(url: string): void {
    this.convexClient = new ConvexAuthClient(url);
    this.useConvexAuth = !!url;
  }

  // =========== Public Methods ===========

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.session !== null && this.session.expiresAt > Date.now();
  }

  /**
   * Get current auth level
   */
  getAuthLevel(): AuthLevel {
    if (!this.session) return "none";
    if (Date.now() > this.session.expiresAt) {
      this.clearSession();
      return "none";
    }
    return this.session.level;
  }

  /**
   * Get current session data
   */
  getSession(): SessionData | null {
    if (!this.session || Date.now() > this.session.expiresAt) {
      this.clearSession();
      return null;
    }
    return { ...this.session };
  }

  /**
   * Local password authentication
   * Does NOT require passkey - bypass mode for non-biometric scenarios
   */
  async loginPassword(password: string): Promise<SessionData> {
    // Validate password
    let authLevel: AuthLevel = "none";

    // Try Convex verification first if available
    if (this.convexClient) {
      try {
        await this.convexClient.registerPasswordKey(password);
        authLevel = this.convexClient.verifyPassword(password);
      } catch {
        // Fall back to local verification
        authLevel = this.verifyPasswordLocal(password);
      }
    } else {
      // Local-only verification
      authLevel = this.verifyPasswordLocal(password);
    }

    if (authLevel === "none") {
      throw new Error("Invalid key");
    }

    return this.setSession({
      level: authLevel,
      method: "password",
      expiresAt: Date.now() + SESSION_EXPIRY_HOURS * 60 * 60 * 1000,
    });
  }

  /**
   * Login with passkey (WebAuthn) through kernel
   */
  async loginPasskey(): Promise<SessionData> {
    // Check kernel availability
    const kernelAvailable = await this.checkKernelAvailable();

    if (!kernelAvailable) {
      throw new Error("Local kernel not available for passkey authentication");
    }

    // Option 1: Get challenge from kernel and register locally
    const optionsRes = await fetch(`${KERNEL_BASE}/auth/login/options`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });

    const payload = await readJsonOrText(optionsRes);
    if (!optionsRes.ok) {
      throw new Error(payload.error ?? `Login options failed: ${optionsRes.status}`);
    }

    const { challengeToken, options } = payload;

    // Convert options to proper format
    const publicKey: PublicKeyCredentialRequestOptions = {
      ...options,
      challenge: base64urlToArrayBuffer(String(options.challenge)),
      allowCredentials: options.allowCredentials?.map((c: any) => ({
        ...c,
        id: base64urlToArrayBuffer(String(c.id)),
      })) || [],
    };

    const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
    if (!cred) throw new Error("Passkey auth cancelled");

    const verifyRes = await fetch(`${KERNEL_BASE}/auth/login/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        challengeToken,
        response: credentialToAuthenticationJSON(cred),
      }),
    });

    const json = await readJsonOrText(verifyRes);
    if (!verifyRes.ok || json.success !== true || typeof json.sessionToken !== "string") {
      throw new Error(json.error ?? "Login verify failed");
    }

    // Passkey auth gives creator-level authority
    return this.setSession({
      level: "creator",
      method: "passkey" as const,
      expiresAt: Date.now() + SESSION_EXPIRY_HOURS * 60 * 60 * 1000,
      token: json.sessionToken,
    });
  }

  /**
   * Two-factor authentication: Password + Passkey through Convex
   * This is the most secure method
   */
  async loginTwoFactor(
    password: string,
    useConvex: boolean = true
  ): Promise<SessionData> {
    // Verify password first
    let authLevel: AuthLevel;

    if (useConvex && this.convexClient) {
      // Use Convex verification
      await this.convexClient.registerPasswordKey(password);
      authLevel = this.convexClient.verifyPassword(password);
    } else {
      // Local verification
      authLevel = this.verifyPasswordLocal(password);
    }

    if (authLevel === "none") {
      throw new Error("Invalid password key");
    }

    // Second factor: passkey is optional but recommended
    // For now, password-only creates a session
    return this.setSession({
      level: authLevel,
      method: "password",
      expiresAt: Date.now() + SESSION_EXPIRY_HOURS * 60 * 60 * 1000,
    });
  }

  /**
   * Register new user
   */
  async register(data: {
    email: string;
    password: string;
    name?: string;
  }): Promise<{ success: boolean; error?: string }> {
    // Simulate registration - in production, this would call an API
    // For now, we'll store a mock user record
    const users = JSON.parse(localStorage.getItem("binetic_users") || "{}");
    
    if (users[data.email]) {
      return { success: false, error: "User already exists" };
    }
    
    users[data.email] = {
      email: data.email,
      password: data.password,
      name: data.name || data.email.split("@")[0],
      role: "user",
      createdAt: Date.now(),
    };
    
    localStorage.setItem("binetic_users", JSON.stringify(users));
    return { success: true };
  }

  /**
   * Register passkey for a user
   */
  async registerPasskey(email: string): Promise<{ success: boolean; error?: string }> {
    // Simulate passkey registration
    // In production, this would use WebAuthn
    return { success: true };
  }

  /**
   * Logout
   */
  logout(): void {
    const session = this.session;
    if (session && this.convexClient && session.convexSessionId) {
      // End session in Convex
      try {
        this.convexClient.endSession(session.convexSessionId);
      } catch {
        // Ignore errors
      }
    }

    this.clearSession();
    this.notifyListeners();
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Validate current session with Convex (if available)
   */
  async validateSession(): Promise<boolean> {
    if (!this.session) return false;

    // Check expiry
    if (Date.now() > this.session.expiresAt) {
      this.clearSession();
      return false;
    }

    // Validate with Convex if using Convex auth
    if (this.convexClient && this.session.convexSessionId) {
      try {
        const validation = await this.convexClient.validateSession(this.session.convexSessionId);
        if (!validation.valid) {
          this.clearSession();
          return false;
        }
      } catch {
        // Convex unavailable - allow session to continue
        // but mark as degraded
        this.session.convexValidated = false;
        return true;
      }
    }

    return true;
  }

  // =========== Private Methods ===========

  /**
   * Local password verification (fallback)
   */
  private verifyPasswordLocal(pwd: string): AuthLevel {
    if (pwd === CREATOR_KEY && CREATOR_KEY) return "creator";
    if (pwd === MASTER_KEY && MASTER_KEY) return "master";
    return "none";
  }

  /**
   * Check if running in production (HTTPS deployed site)
   */
  private isProduction(): boolean {
    return typeof window !== "undefined" && window.location.protocol === "https:";
  }

  /**
   * Check if kernel is available
   */
  private async checkKernelAvailable(): Promise<boolean> {
    // Skip localhost kernel checks from production (HTTPS can't call HTTP localhost)
    if (this.isProduction() && KERNEL_BASE.includes("127.0.0.1")) {
      return false;
    }

    try {
      const response = await fetch(`${KERNEL_BASE}/status`, {
        method: "GET",
        signal: AbortSignal.timeout(5000),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Set session and persist it
   */
  private setSession(session: SessionData): SessionData {
    this.session = session;
    this.persistSession();
    this.notifyListeners();
    return session;
  }

  /**
   * Load session from localStorage
   */
  private loadSession(): void {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.expiresAt > Date.now()) {
          this.session = parsed;
        } else {
          this.clearSession(false);
        }
      } catch {
        this.clearSession(false);
      }
    }
  }

  /**
   * Persist session to localStorage
   */
  private persistSession(): void {
    if (this.session) {
      localStorage.setItem(SESSION_KEY, JSON.stringify(this.session));
    }
  }

  /**
   * Clear session from memory and storage
   */
  private clearSession(notify: boolean = true): void {
    this.session = null;
    localStorage.removeItem(SESSION_KEY);
    if (notify) this.notifyListeners();
  }

  /**
   * Notify all subscribers of auth state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach((callback) => callback());
  }

  /**
   * Periodic session check to auto-expire and validate
   */
  private sessionCheckInterval(): void {
    // Check every 2 minutes
    setInterval(async () => {
      if (this.session && Date.now() > this.session.expiresAt) {
        this.clearSession();
      }

      // Validate session with Convex periodically
      if (this.convexClient && this.session?.convexSessionId) {
        await this.validateSession();
      }
    }, 120000);
  }
}

// Export singleton instance
export const authService = new AuthService();
